<template>
    <div class="roles-index-container animated fadeIn">
        <b-card class="roles-card">
            <div class="roles-header mb-4">
                <div>
                    <h4>{{ $t('roles') }}</h4>
                    <p class="text-muted mb-0">{{ $t('manageRolesDescription') }}</p>
                </div>
                <b-button variant="primary" size="lg" @click="createRole">
                    <b-icon icon="plus-circle"></b-icon> {{ $t('createRole') }}
                </b-button>
            </div>

            <b-form-input
                v-model="searchFilter"
                :placeholder="$t('filterRoles')"
                size="lg"
                class="mb-4"
            >
                <template #prepend>
                    <b-icon icon="search"></b-icon>
                </template>
            </b-form-input>

            <div class="roles-grid">
                <div 
                    v-for="role in filteredItems" 
                    :key="role.uuid"
                    class="role-card"
                    @click="edit(role)"
                >
                    <div class="role-card-header">
                        <div class="role-info">
                            <h5 class="role-name">{{ role.name }}</h5>
                            <div class="role-meta">
                                <b-badge variant="dark" pill class="mr-2 level-badge">
                                    <b-icon icon="shield"></b-icon> {{ $t('level') }}: {{ role.level }}
                                </b-badge>
                                <b-badge variant="secondary" pill v-if="role.permissions_count">
                                    <b-icon icon="key"></b-icon> {{ role.permissions_count }} {{ $t('permissions') }}
                                </b-badge>
                            </div>
                        </div>
                        <b-icon icon="chevron-right" class="role-arrow"></b-icon>
                    </div>

                    <div class="role-tags" v-if="role.tags && role.tags.length > 0">
                        <b-badge 
                            v-for="tag in role.tags" 
                            :key="tag"
                            variant="primary"
                            class="tag-badge"
                        >
                            {{ tag }}
                        </b-badge>
                    </div>

                    <div class="role-footer" v-if="role.workspaces && role.workspaces.length > 0">
                        <div class="workspaces-info">
                            <b-icon icon="grid3x3"></b-icon>
                            <span>{{ role.workspaces.length }} {{ $t('workspaces') }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="filteredItems.length === 0" class="no-roles">
                <b-icon icon="inbox" font-scale="3" class="mb-3"></b-icon>
                <h5>{{ $t('noRolesFound') }}</h5>
                <p class="text-muted">{{ searchFilter ? $t('tryDifferentSearch') : $t('createFirstRole') }}</p>
            </div>
        </b-card>
    </div>
</template>

<script>
export default {
    name: "Users",
    props: {
        caption: {
        type: String,
        default: 'Users'
        },
        hover: {
        type: Boolean,
        default: true
        },
        striped: {
        type: Boolean,
        default: true
        },
        bordered: {
        type: Boolean,
        default: false
        },
        small: {
        type: Boolean,
        default: false
        },
        fixed: {
        type: Boolean,
        default: false
        }
    },
    computed: {
        items() {
            return this.$store.getters.getRoles
        },
        filteredItems() {
            if (!this.searchFilter) {
                return this.items
            }
            
            const search = this.searchFilter.toLowerCase()
            return this.items.filter(role => {
                const name = role.name ? role.name.toLowerCase() : ''
                const level = role.level ? role.level.toString() : ''
                const tags = role.tags ? role.tags.join(' ').toLowerCase() : ''
                
                return name.includes(search) || level.includes(search) || tags.includes(search)
            })
        }
    },
    methods: {
        edit(item) {
            this.$router.push({path:"/admin/iam/roles/edit/" + item.uuid})
        },
        createRole() {
            this.$router.push({path: this.$route.path + "/create"})
        }
    },
    data: function() {
        return {
            searchFilter: ''
        }
    },
    mounted() {
        this.$store.dispatch("fetchRoles", true)
    }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.roles-index-container {
    max-width: 1400px;
    margin: 0 auto;
}

.roles-card {
    .roles-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        
        h4 {
            color: var(--text-primary);
            font-weight: 600;
            margin: 0 0 4px 0;
        }
        
        p {
            font-size: 14px;
        }
    }
}

.roles-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
    margin-top: 20px;
}

.role-card {
    background: $dark-hard2-background;
    border: 1px solid $dark-border2;
    border-radius: 12px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
        border-color: $protokol-royal-active;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
        transform: translateY(-2px);
        
        .role-arrow {
            transform: translateX(4px);
            color: $protokol-royal-active;
        }
    }
    
    .role-card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 16px;
        
        .role-info {
            flex: 1;
            
            .role-name {
                color: var(--text-primary);
                font-size: 18px;
                font-weight: 600;
                margin: 0 0 12px 0;
            }
            
            .role-meta {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                
                .badge {
                    font-size: 12px;
                    font-weight: 500;
                    padding: 6px 12px;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                }
                
                .level-badge {
                    background: var(--border-default);
                    color: rgba(255, 255, 255, 0.9);
                    border: 1px solid var(--border-default);
                }
            }
        }
        
        .role-arrow {
            color: rgba(255, 255, 255, 0.4);
            font-size: 20px;
            transition: all 0.2s;
            margin-top: 4px;
        }
    }
    
    .role-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        padding: 12px 0;
        border-top: 1px solid $dark-border2;
        
        .tag-badge {
            font-size: 11px;
            font-weight: 500;
            padding: 4px 10px;
            background: rgba(102, 126, 234, 0.2);
            color: $protokol-royal-active;
            border: 1px solid rgba(102, 126, 234, 0.3);
        }
    }
    
    .role-footer {
        padding-top: 12px;
        border-top: 1px solid $dark-border2;
        
        .workspaces-info {
            display: flex;
            align-items: center;
            gap: 8px;
            color: var(--text-secondary);
            font-size: 13px;
            
            svg {
                color: rgba(255, 255, 255, 0.4);
            }
        }
    }
}

.no-roles {
    text-align: center;
    padding: 60px 20px;
    
    svg {
        color: var(--text-muted);
    }
    
    h5 {
        color: var(--text-primary);
        margin-bottom: 8px;
    }
    
    p {
        font-size: 14px;
    }
}
</style>
